import type { APIManufacturerAliasReference } from '@visorpro/client'
import type { APIUpdateOrganizationProductManufacturerAliasRequest } from '@visorpro/client/dist/RestClient/services'
import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { visorPRORestClient } from '../../../../util/api'
import { observed } from '../../../../util/observed-decorator'
import { useEffect, useMemo } from 'react'

export class WOManufacturerMatchStore {
    public aliasesById: Record<string, APIManufacturerAliasReference> = {}
    public aliasesByInputManufacturer: Record<
        string,
        APIManufacturerAliasReference
    > = {}
    public inputManufacturers: string[] = []
    // The api's count, which can be more than the `limit` loaded.
    public total = 0
    public isFetched = false
    public isFetching = false
    public isUpdating = false

    constructor(private organizationId: string) {
        makeAutoObservable(this)
    }

    @observed('isFetching')
    public async load() {
        try {
            const response =
                await visorPRORestClient.organizationManufacturerAlias.list(
                    this.organizationId,
                    {
                        offset: 0,
                        limit: 1000,
                    },
                )

            const byId: Record<string, APIManufacturerAliasReference> = {}
            const byInputManufacturer: Record<
                string,
                APIManufacturerAliasReference
            > = {}
            const inputManufacturers: string[] = []

            response.items.forEach((manufacturer) => {
                byInputManufacturer[manufacturer.input_manufacturer] =
                    manufacturer
                inputManufacturers.push(manufacturer.input_manufacturer)

                if (manufacturer.alias_id) {
                    byId[manufacturer.alias_id] = manufacturer
                }
            })

            runInAction(() => {
                this.aliasesById = byId
                this.aliasesByInputManufacturer = byInputManufacturer
                this.inputManufacturers = inputManufacturers
                this.total = response.total
                this.isFetched = true
            })
        } catch (e) {
            toast.error(
                `Failed to fetch manufacturer aliases: ${(e as Error).message}`,
            )
        }
    }

    @observed('isUpdating')
    public async update(
        aliasId: string,
        input: APIUpdateOrganizationProductManufacturerAliasRequest,
    ) {
        try {
            await visorPRORestClient.organizationManufacturerAlias.update(
                this.organizationId,
                aliasId,
                input,
            )
            await this.load()
        } catch (e) {
            toast.error(
                `Failed to update manufacturer alias: ${(e as Error).message}`,
            )
        }
    }

    @observed('isUpdating')
    public async upsertByInputManufacturer(
        inputManufacturer: string,
        input: APIUpdateOrganizationProductManufacturerAliasRequest,
    ) {
        try {
            const current =
                this.aliasesByInputManufacturer[inputManufacturer] ?? null

            if (current?.alias_id) {
                await visorPRORestClient.organizationManufacturerAlias.update(
                    this.organizationId,
                    current.alias_id,
                    input,
                )
                await this.load()
                return
            }

            const created =
                await visorPRORestClient.organizationManufacturerAlias.create(
                    this.organizationId,
                    {
                        input_manufacturer: inputManufacturer,
                        product_manufacturer_id:
                            input.product_manufacturer_id ?? null,
                    },
                )

            if (input.is_reviewed) {
                await visorPRORestClient.organizationManufacturerAlias.update(
                    this.organizationId,
                    created.id,
                    input,
                )
            }

            await this.load()
        } catch (e) {
            toast.error(
                `Failed to create/update manufacturer alias: ${(e as Error).message}`,
            )
        }
    }
}

export const useManufacturersAliases = (store: WOManufacturerMatchStore) => {
    useEffect(() => {
        if (!store.isFetched && !store.isFetching) {
            void store.load()
        }
    }, [store])

    return useMemo(() => {
        const manufacturers: APIManufacturerAliasReference[] = Object.values(
            store.aliasesByInputManufacturer,
        )
        manufacturers.sort((a, b) => {
            if (a.work_order_segments_count !== b.work_order_segments_count) {
                return b.work_order_segments_count - a.work_order_segments_count
            }

            return a.input_manufacturer.localeCompare(b.input_manufacturer)
        })
        return manufacturers
    }, [store.aliasesByInputManufacturer])
}

export const useResolvedManufacturerAlias = (
    row: { manufacturer_alias_id?: string; input_manufacturer: string },
    store: WOManufacturerMatchStore,
) => {
    const byKey = useManufacturersAliasById(row.manufacturer_alias_id, store)
    const byName = store.aliasesByInputManufacturer[row.input_manufacturer]
    return byKey ?? byName
}

export const useManufacturersAliasById = (
    id: string | undefined,
    store: WOManufacturerMatchStore,
): APIManufacturerAliasReference | undefined => {
    useEffect(() => {
        if (!store.isFetched && !store.isFetching) {
            void store.load()
        }
    }, [store])

    if (id) {
        return store.aliasesById[id]
    }
}
